import { type FC, useEffect, useState } from 'react';

import Input from '../Input';
import Modal from '../Modal';
import { StyledLinkDialogBody } from './RichTextEditor.styled';

type LinkDialogProps = {
  open: boolean;
  initialHref: string;
  hasLink: boolean;
  onClose: () => void;
  onApply: (_href: string) => void;
  onRemove: () => void;
};

const HREF_PATTERN = /^(https?:\/\/|mailto:)/i;

const normalizeHref = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  return HREF_PATTERN.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const LinkDialog: FC<LinkDialogProps> = ({
  open,
  initialHref,
  hasLink,
  onClose,
  onApply,
  onRemove,
}) => {
  const [href, setHref] = useState(initialHref);

  useEffect(() => {
    if (open) setHref(initialHref);
  }, [open, initialHref]);

  const normalized = normalizeHref(href);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      variant="center"
      title={hasLink ? 'Edit link' : 'Add link'}
      maxWidth={520}
      showClose
      primaryAction={{
        label: 'Apply',
        disabled: !HREF_PATTERN.test(normalized),
        onClick: () => onApply(normalized),
      }}
      secondaryAction={
        hasLink
          ? { label: 'Remove', color: 'tertiary', variant: 'outline', onClick: onRemove }
          : { label: 'Cancel', variant: 'outline', onClick: onClose }
      }
    >
      <StyledLinkDialogBody>
        <Input
          label="URL"
          autoComplete="off"
          placeholder="https://…"
          value={href}
          onChange={(event) => setHref(event.currentTarget.value)}
        />
      </StyledLinkDialogBody>
    </Modal>
  );
};
